import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import Icon from '../common/Icon';
import { useProgressContext } from '../../contexts/ProgressContext';
import { useUIContext } from '../../contexts/UIContext';
import logger from '../../utils/logger';

const InstantUnlockModal = ({ isOpen, onClose, onConfirmSwitch, isLoading = false }) => {
  const { setPage } = useUIContext();
  const { progress } = useProgressContext();
  const subscriptionStatus = progress?.subscriptionStatus;
  const isTrialUser = subscriptionStatus === 'trial' || subscriptionStatus === 'trial_expired';

  // Debug logging
  logger.info('InstantUnlockModal', 'Info', isOpen, 'isTrialUser:', isTrialUser);

  // Handle scroll locking and cleanup when modal opens - CRITICAL for preventing button timeouts
  useEffect(() => {
    if (!isOpen) return;

    // Prevent background scrolling
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    // Force the modal to appear in the center of the current viewport
    const currentScrollY = window.scrollY;
    if (currentScrollY > 0) {
      // If we're scrolled down, scroll to the top so the modal is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    
    // CRITICAL CLEANUP - Prevents button timeout issues
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscape);
      
      // Restore scroll position after modal closes
      if (currentScrollY > 0) {
        setTimeout(() => {
          window.scrollTo({ top: currentScrollY, behavior: 'smooth' });
        }, 100);
      }

      // Force cleanup any lingering active states
      setTimeout(() => {
        const activeElements = document.querySelectorAll('[aria-expanded="true"]');
        activeElements.forEach(el => el.setAttribute('aria-expanded', 'false'));
        
        // Remove any stuck focus states
        const focusedElements = document.querySelectorAll(':focus');
        focusedElements.forEach(el => el.blur());
      }, 150);
    };
  }, [isOpen, onClose]);

  const handleMaybeLater = () => {
    logger.info('InstantUnlockModal', 'Info');
    onClose();
  };

  const handleSeePlans = () => {
    logger.info('InstantUnlockModal', 'Info');
    onClose();
    setPage('subscription_page');
  };

  const handleConfirmSwitch = () => {
    logger.info('InstantUnlockModal', 'Info');
    onConfirmSwitch();
  };

  // Trial user modal content
  const trialUserContent = (
    <div className="text-center">
      <span className="text-6xl mb-4 block">🔒</span>
      <h2 className="text-2xl font-extrabold mb-2 text-gray-900">
        Unlock Full Access
      </h2>
      <p className="text-gray-600 mb-4">
        This lesson is not included in the free trial. Subscribe to unlock all units and 100+ lessons, stories, and activities.
      </p>
      <div className="space-y-3 mb-6 text-left">
        <div className="flex items-center">
          <span className="text-green-500 mr-3 flex-shrink-0">✅</span>
          <span className="text-sm">Access all units and 100+ lessons</span>
        </div>
        <div className="flex items-center">
          <span className="text-green-500 mr-3 flex-shrink-0">✅</span>
          <span className="text-sm">Unlock all Story Time chapters</span>
        </div>
        <div className="flex items-center">
          <span className="text-green-500 mr-3 flex-shrink-0">✅</span>
          <span className="text-sm">Get unlimited Game Zone plays</span>
        </div>
        <div className="flex items-center">
          <span className="text-green-500 mr-3 flex-shrink-0">✅</span>
          <span className="text-sm">Track progress across devices</span>
        </div>
      </div>
      <div className="space-y-3">
        <button
          onClick={handleSeePlans}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition active:scale-95"
        >
          See Plans
        </button>
        <button
          onClick={handleMaybeLater}
          className="w-full text-gray-600 hover:text-gray-800 font-bold py-3 transition"
        >
          Maybe Later
        </button>
      </div>
    </div>
  );

  // Non-trial user modal content
  const nonTrialUserContent = (
    <div className="text-center">
      <span className="text-5xl mb-2 inline-block"><Icon name="lightbulb" /></span>
      <h2 className="text-2xl font-extrabold mt-2 mb-2 text-gray-900">
        Unlock All Lessons?
      </h2>
      <p className="text-gray-600 mb-3">
        You're currently on the Guided Path, where new units unlock as you go. Would you like to switch to Explorer Mode to make all units available right now?
      </p>
      <p className="text-xs text-gray-500 mb-6">
        (You can always switch back in the Parental Dashboard.)
      </p>
      <div className="space-y-3">
        <button
          onClick={handleConfirmSwitch}
          disabled={isLoading}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading && (
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {isLoading ? 'Unlocking...' : 'Yes, Unlock Everything!'}
        </button>
        <button
          onClick={handleMaybeLater}
          disabled={isLoading}
          className="w-full text-gray-600 hover:text-gray-800 font-bold py-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          No, Stay on the Path
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  // Create portal root if it doesn't exist
  let portalRoot = document.getElementById('modal-portal-root');
  if (!portalRoot) {
    portalRoot = document.createElement('div');
    portalRoot.id = 'modal-portal-root';
    portalRoot.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      pointer-events: none !important;
      z-index: 2147483647 !important;
      transform: translateZ(0) !important;
      will-change: auto !important;
      filter: none !important;
      perspective: none !important;
      isolation: isolate !important;
    `;
    document.body.appendChild(portalRoot);
  }

  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        zIndex: 2147483647,
        pointerEvents: 'auto',
        transform: 'translateZ(0)', // Force hardware acceleration
        willChange: 'auto',
        isolation: 'isolate'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          padding: '1.5rem',
          width: '100%',
          maxWidth: isTrialUser ? '28rem' : '24rem',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          zIndex: 2147483648,
          transform: 'translateZ(0)', // Force hardware acceleration
          willChange: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isTrialUser ? trialUserContent : nonTrialUserContent}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, portalRoot);
};

export default InstantUnlockModal;